<template>
  <div v-if="!hasChartListsToShow && !savedChartsStore.hasFailedToLoadChartLists" class="chart-list-summary-group-message">
    <span>It looks like you haven't created any ChartLists&nbsp;yet!</span>
      <br />
      <br />
      <button
        type="button"
        class="sc3-button sc3-button-green"
        @click="launchCreateChartListModal()"
      >Create ChartList</button>
  </div>
  <div v-if="!hasChartListsToShow && savedChartsStore.hasFailedToLoadChartLists" class="chart-list-summary-group-message">
    <span class="chart-list-summary-group-message-small-note">Failed to load ChartLists.</span>
  </div>
  <div v-if="hasChartListsToShow" class="chart-list-summary-ui-container">
    <div class="chart-list-summary-ui-components">
      <div class="chart-list-summary-ui-upper-bar">
        <div class="chart-list-secondary-control-parent">
          <div>
            <label for="chart-list-summary-chart-lists" class="sc3-form-label sc3-form-label-inline sc3-form-label-small">SELECT CHARTLIST</label>
            <br />
            <select
              class="sc3-select-menu sc3-select-menu-inline"
              id="chart-list-summary-chart-lists"
              v-model="selectedChartListRecid"
            >
              <option
                v-for="chartList in chartLists"
                :key="chartList.recid"
                :value="chartList.recid"
                :disabled="chartList.isFrameworkChartList"
              >{{ chartList.htmlSanitizedName }}</option>
            </select>
          </div>
          <ViewAsDropdownMenu
            v-if="selectedChartList"
            :chart-list="selectedChartList"
            :is-inline="true"
            dropdown-menu-toggle-button-css-color-sc3-button-class="sc3-button-white-and-light-grey"
          />
          <button
            :disabled="!selectedChartList?.charts.length || !selectedRows.length || selectedChartList.isReadonly"
            type="button"
            class="sc3-button sc3-button-grey sc3-button-inline sc3-button-last apply-chart-style-button"
            @click="applyChartStyleToCharts()"
          >
            <i class="fas fa-fw fa-cogs" aria-hidden="true"></i>
            &nbsp;Apply ChartStyle
          </button>
          <div class="show-symbols-only-checkbox-container">
            <SCThreeCheckbox
              id="chart-list-summary-ui-load-symbol-only-checkbox"
              label-text="Load Symbol Only"
              :checkbox-value="isLoadSymbolOnlyEnabled"
              @update:checkbox-value="setIsLoadSymbolOnlyEnabled"
              :is-small-font="true"
              :is-circle="true"
              :is-single-spaced="true"
              :is-lato-font="true"
            />
          </div>
        </div>
        <div class="primary-summary-table-controls-parent">
          <div>
            <label for="chart-list-summary-period-select" class="sc3-form-label sc3-form-label-inline sc3-form-label-small">PERIOD</label>
            <br />
            <select
              class="sc3-select-menu sc3-select-menu-inline"
              id="chart-list-summary-period-select"
              v-model="periodType"
            >
              <option value="now">Intraday</option>
              <option value="endOfDay">End-of-Day</option>
              <option value="oneWeek">One Week</option>
              <option value="oneMonth">One Month</option>
              <option value="threeMonths">Three Month</option>
              <option value="sixMonths">Six Month</option>
              <option value="yearToDate">YTD</option>
              <option value="oneYear">One Year</option>
            </select>
          </div>
          <ChartListSummaryColumnVisibilityDropdownMenu />
          <div class="search-chart-list-summary-parent">
            <label
              for="search-chart-list-summary-input"
              class="sc3-form-label sc3-form-label-inline sc3-form-label-small"
            >SEARCH CHARTLIST SUMMARY</label>
            <br />
            <input
              type="text"
              class="sc3-text-input sc3-text-input-inline sc3-text-input-full-width"
              id="search-chart-list-summary-input"
              placeholder="Search"
              v-model="filterText"
              ref="filterInput"
              autocomplete="off"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="hasSymbolSummaryModeHadAnError" class="error-message">
      Unable to load ChartList Summary for {{ selectedChartList?.name ?? 'this ChartList' }}
    </div>
    <div v-else-if="isSymbolSummaryModeLoading" class="chart-list-summary-group-message loading-block">
      <i class="fad fa-fw fa-spinner-third fa-spin"></i>
    </div>
    <div v-else-if="!chartsOfSelectedChartList.length" class="chart-list-summary-group-message">
      <span>It looks like this {{ selectedChartList?.isReadonly ? 'read-only' : '' }} ChartList is&nbsp;empty!</span>
      <br>
      <br>
      <span class="chart-list-summary-group-message-small-note">Save and organize the charts you're watching with your own&nbsp;ChartLists.</span>
      <br v-if="!selectedChartList?.isReadonly">
      <br v-if="!selectedChartList?.isReadonly">
      <a
        v-if="!selectedChartList?.isReadonly"
        :href="`/panels?obj=${chartCode},${selectedChartList?.listNum}&cmd=show&disp=c`"
        class="sc3-button sc3-button-green"
      >Add Symbols</a>
    </div>
    <div v-else class="chart-list-summary-table-container">
      <div class="table-responsive-overflow">
        <table v-show="areAnyRowsVisible" class="sc3-table data-table" aria-label="Data Table">
          <colgroup>
            <col
              v-for="column in rowData"
              :key="column.recid"
            />
          </colgroup>
          <thead>
            <tr>
              <th scope="col" class="select-all-rows-header-container">
                <button class="select-all-button" type="button" @click="toggleAllRows()" title="Toggle All Rows">
                  <i class="fas fa-fw fa-check-square" aria-hidden="true"></i>
                </button>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.symbol" @click="setSorting('symbol', 'ascending')" >
                <span class="sortable-header-container">
                  Symbol
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'symbol', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'symbol' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.name" @click="setSorting('htmlSanitizedName', 'ascending')">
                <span class="sortable-header-container">
                  Name
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'htmlSanitizedName', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'htmlSanitizedName' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.sector" @click="setSorting('sector', 'ascending')" >
                <span class="sortable-header-container">
                  Sector
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'sector', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'sector' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.industry" @click="setSorting('industry', 'ascending')" >
                <span class="sortable-header-container">
                  Industry
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'industry', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'industry' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.last" @click="setSorting('last', 'descending')" >
                <span class="sortable-header-container">
                  Last
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'last', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'last' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.change" @click="setSorting('change', 'descending')" >
                <span class="sortable-header-container">
                  CHG
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'change', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'change' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.percentChange" @click="setSorting('percentChange', 'descending')" >
                <span class="sortable-header-container">
                  % CHG
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'percentChange', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'percentChange' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.plusMinus" @click="setSorting('changeBarGradientCoefficient', 'descending')" >
                <span class="sortable-header-container plus-minus-change-container">
                  +/-
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'changeBarGradientCoefficient', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'changeBarGradientCoefficient' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.volume" @click="setSorting('volume', 'descending')" >
                <span class="sortable-header-container">
                  Volume
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'volume', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'volume' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.marketCap" @click="setSorting('marketCap', 'descending')" >
                <span class="sortable-header-container">
                  Market Cap
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'marketCap', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'marketCap' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.sctr" @click="setSorting('sctr', 'descending')" >
                <span class="sortable-header-container">
                  SCTR
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'sctr', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'sctr' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.universe" @click="setSorting('universeNumericSize', 'ascending')" >
                <span class="sortable-header-container">
                  U
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'universeNumericSize', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'universeNumericSize' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.emaTwenty" @click="setSorting('emaTwentyDifferencePercentage', 'descending')" >
                <span class="sortable-header-container">
                  EMA (20)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'emaTwentyDifferencePercentage', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'emaTwentyDifferencePercentage' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.smaFifty" @click="setSorting('smaFiftyDifferencePercentage', 'descending')" >
                <span class="sortable-header-container">
                  SMA (50)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'smaFiftyDifferencePercentage', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'smaFiftyDifferencePercentage' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.smaTwoHundred" @click="setSorting('smaTwoHundredDifferencePercentage', 'descending')" >
                <span class="sortable-header-container">
                  SMA (200)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'smaTwoHundredDifferencePercentage', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'smaTwoHundredDifferencePercentage' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.rsi" @click="setSorting('rsi', 'descending')" >
                <span class="sortable-header-container">
                  RSI (14)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'rsi', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'rsi' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.atr" @click="setSorting('atr', 'descending')" >
                <span class="sortable-header-container">
                  ATR (14)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'atr', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'atr' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.adx" @click="setSorting('adx', 'descending')" >
                <span class="sortable-header-container">
                  ADX (14)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'adx', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'adx' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.nextEarnings" @click="setSorting('nextEarningsNumericValue', 'descending')" >
                <span class="sortable-header-container">
                  Next Earnings
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'nextEarningsNumericValue', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'nextEarningsNumericValue' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.lastEarnings" @click="setSorting('lastEarningsNumericValue', 'descending')" >
                <span class="sortable-header-container">
                  Last Earnings
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'lastEarningsNumericValue', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'lastEarningsNumericValue' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.dividendTTM" @click="setSorting('dividendTTM', 'descending')" >
                <span class="sortable-header-container">
                  Dividend (TTM)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'dividendTTM', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'dividendTTM' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.yieldTTM" @click="setSorting('yieldTTM', 'descending')" >
                <span class="sortable-header-container">
                  Yield (TTM)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'yieldTTM', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'yieldTTM' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.dividendFTM" @click="setSorting('dividendFTM', 'descending')" >
                <span class="sortable-header-container">
                  Dividend (FTM)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'dividendFTM', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'dividendFTM' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.yieldFTM" @click="setSorting('yieldFTM', 'descending')" >
                <span class="sortable-header-container">
                  Yield (FTM)
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'yieldFTM', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'yieldFTM' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.dividendExDate" @click="setSorting('dividendExDateNumericValue', 'descending')" >
                <span class="sortable-header-container">
                  Ex-Dividend Date
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'dividendExDateNumericValue', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'dividendExDateNumericValue' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.epsTTM" @click="setSorting('epsTTM', 'descending')">
                <span class="sortable-header-container">
                  EPS
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'epsTTM', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'epsTTM' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.priceOverEarnings" @click="setSorting('priceOverEarnings', 'descending')">
                <span class="sortable-header-container">
                  P/E
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'priceOverEarnings', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'priceOverEarnings' }"></i>
                </span>
              </th>
              <th scope="col" class="sortable" v-show="shownColumns.date" @click="setSorting('dateNumericValue', 'descending')">
                <span class="sortable-header-container">
                  Date
                  <i class="fas fa-sort" :class="{ 'fa-sort-amount-down': sortDirection === 'descending' && sortKey === 'dateNumericValue', 'fa-sort-amount-up': sortDirection === 'ascending' && sortKey === 'dateNumericValue' }"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rowData"
              :key="row.recid"
            >
              <td class="row-select-checkbox">
                <SCThreeCheckbox
                  :id="`data-table-row-select-${row.recid}`"
                  @update:checkbox-value="handleRowSelectionChange($event, row)"
                  :checkbox-value="selectedRowRecids.includes(row.recid)"
                  :isSquare="true"
                  justify="center"
                />
              </td>
              <td v-show="shownColumns.symbol">
                <button
                  type="button"
                  class="clickable-row-text"
                  @click="loadSimpleFrontendChart(row)"
                  title="Load Chart"
                >{{ row.symbol }}</button>
              </td>
              <td v-show="shownColumns.name" v-sanitize-html="row.name"></td>
              <td v-show="shownColumns.sector">{{ row.sector }}</td>
              <td v-show="shownColumns.industry">{{ row.industry }}</td>
              <td
                v-show="shownColumns.last"
                class="right-align"
              >
                {{ typeof row.last === 'number' ? formatNumber(row.last, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}
              </td>
              <td
                v-show="shownColumns.change"
                class="align-right"
                :class="{
                'green-font': typeof row.change === 'number' && row.change > 0,
                'red-font': typeof row.change === 'number' && row.change < 0
              }">
                {{ typeof row.change === 'number' ? formatNumber(row.change, { includeSign: true, desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}
              </td>
              <td
                v-show="shownColumns.percentChange"
                class="align-right"
                :class="{
                  'green-font': typeof row.percentChange === 'number' && row.percentChange > 0,
                  'red-font': typeof row.percentChange === 'number' && row.percentChange < 0
                }"
              >
                {{ typeof row.percentChange === 'number' ? formatNumber(row.percentChange, { includeSign: true, desiredNumberOfDecimals: 2, shouldInsertCommas: true }) + '%' : '' }}
              </td>
              <td v-show="shownColumns.plusMinus">
                <span
                  v-if="typeof row.changeBarGradientCoefficient === 'number'"
                  class="gradient-bar"
                  :class="{
                    'red-gradient-bar': row.changeBarGradientCoefficient < 0,
                    'green-gradient-bar': row.changeBarGradientCoefficient > 0
                  }"
                  :style="{ width: `${ Math.round(Math.abs(row.changeBarGradientCoefficient) * 100) }%` }"
                ></span>
              </td>
              <td
                v-show="shownColumns.volume"
                class="align-right"
              >{{ typeof row.volume === 'number' && row.volume !== 0 ? formatNumber(row.volume, { shouldInsertCommas: true }) : '' }}</td>
              <td
                v-show="shownColumns.marketCap"
                class="align-right"
              >{{ typeof row.marketCap === 'number' && row.marketCap !== 0 ? formatMarketCap(row.marketCap) : '' }}</td>
              <td
                v-show="shownColumns.sctr"
                class="sctr align-right"
                :class="{
                  'sctr-zero-to-twenty': typeof row.sctr === 'number' && row.sctr >= 0 && row.sctr < 20,
                  'sctr-twenty-to-fourty': typeof row.sctr === 'number' && row.sctr >= 20 && row.sctr < 40,
                  'sctr-fourty-to-sixty': typeof row.sctr === 'number' && row.sctr >= 40 && row.sctr < 60,
                  'sctr-sixty-to-eighty': typeof row.sctr === 'number' && row.sctr >= 60 && row.sctr < 80,
                  'sctr-eighty-to-ninety': typeof row.sctr === 'number' && row.sctr >= 80 && row.sctr < 90,
                  'sctr-ninety-plus': typeof row.sctr === 'number' && row.sctr >= 90
                }"
              >
                {{ typeof row.sctr === 'number' ? formatNumber(row.sctr, { desiredNumberOfDecimals: 1, shouldInsertCommas: true }) : '' }}
              </td>
              <td
                v-show="shownColumns.universe"
                class="align-right"
              >{{ row.universe }}</td>
              <td
                v-show="shownColumns.emaTwenty"
                class="align-right"
              >
                <template v-if="typeof row.emaTwenty === 'number'">
                  {{ formatNumber(row.emaTwenty, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) }}
                  (<span
                    :class="{
                      'green-font': typeof row.emaTwentyDifferencePercentage === 'number' && row.emaTwentyDifferencePercentage > 0,
                      'red-font': typeof row.emaTwentyDifferencePercentage === 'number' && row.emaTwentyDifferencePercentage < 0
                    }"
                  >
                    {{ typeof row.emaTwentyDifferencePercentage === 'number' ? formatNumber(row.emaTwentyDifferencePercentage, { desiredNumberOfDecimals: 2, includeSign: true, shouldInsertCommas: true }) + '%' : '' }}
                  </span>)
                </template>
              </td>
              <td
                v-show="shownColumns.smaFifty"
                class="align-right"
              >
                <template v-if="typeof row.smaFifty === 'number'">
                  {{ formatNumber(row.smaFifty, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) }}
                  (<span
                    :class="{
                      'green-font': typeof row.smaFiftyDifferencePercentage === 'number' && row.smaFiftyDifferencePercentage > 0,
                      'red-font': typeof row.smaFiftyDifferencePercentage === 'number' && row.smaFiftyDifferencePercentage < 0
                    }"
                  >
                    {{ typeof row.smaFiftyDifferencePercentage === 'number' ? formatNumber(row.smaFiftyDifferencePercentage, { desiredNumberOfDecimals: 2, includeSign: true, shouldInsertCommas: true }) + '%' : '' }}
                  </span>)
                </template>
              </td>
              <td
                v-show="shownColumns.smaTwoHundred"
                class="align-right"
              >
                <template v-if="typeof row.smaTwoHundred === 'number'">
                  {{ formatNumber(row.smaTwoHundred, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) }}
                  (<span
                    :class="{
                      'green-font': typeof row.smaTwoHundredDifferencePercentage === 'number' && row.smaTwoHundredDifferencePercentage > 0,
                      'red-font': typeof row.smaTwoHundredDifferencePercentage === 'number' && row.smaTwoHundredDifferencePercentage < 0
                    }"
                  >
                    {{ typeof row.smaTwoHundredDifferencePercentage === 'number' ? formatNumber(row.smaTwoHundredDifferencePercentage, { desiredNumberOfDecimals: 2, includeSign: true, shouldInsertCommas: true }) + '%' : '' }}
                  </span>)
                </template>
              </td>
              <td
                v-show="shownColumns.rsi"
                class="align-right"
              >{{ typeof row.rsi === 'number' ? formatNumber(row.rsi, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}</td>
              <td
                v-show="shownColumns.atr"
                class="align-right"
              >{{ typeof row.atr === 'number' ? formatNumber(row.atr, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}</td>
              <td
                v-show="shownColumns.adx"
                class="align-right"
              >{{ typeof row.adx === 'number' ? formatNumber(row.adx, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}</td>
              <td
                v-show="shownColumns.nextEarnings"
                class="align-right"
              >{{ row.nextEarnings }}{{ row.earningsTime ? ` (${row.earningsTime})` : ''}}</td>
              <td
                v-show="shownColumns.lastEarnings"
                class="align-right"
              >{{ row.lastEarnings }}</td>
              <td
                v-show="shownColumns.dividendTTM"
                class="align-right"
              >
                {{ typeof row.dividendTTM === 'number' && row.dividendTTM !== 0 ? formatNumber(row.dividendTTM, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}
              </td>
              <td
                v-show="shownColumns.yieldTTM"
                class="align-right"
              >
                {{ typeof row.yieldTTM === 'number' && row.yieldTTM !== 0 ? formatNumber(row.yieldTTM, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) + '%' : '' }}
              </td>
              <td
                v-show="shownColumns.dividendFTM"
                class="align-right"
              >
                {{ typeof row.dividendFTM === 'number' && row.dividendFTM !== 0 ? formatNumber(row.dividendFTM, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}
              </td>
              <td
                v-show="shownColumns.yieldFTM"
                class="align-right"
              >
                {{typeof row.yieldFTM === 'number' && row.yieldFTM !== 0 ? formatNumber(row.yieldFTM, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) + '%' : '' }}
              </td>
              <td
                v-show="shownColumns.dividendExDate"
                class="align-right"
              >{{ row.dividendExDate }}</td>
              <td
                v-show="shownColumns.epsTTM"
                class="align-right"
              >{{ typeof row.epsTTM === 'number' ? formatNumber(row.epsTTM, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}</td>
              <td
                v-show="shownColumns.priceOverEarnings"
                class="align-right"
              >{{ typeof row.priceOverEarnings === 'number' ? formatNumber(row.priceOverEarnings, { desiredNumberOfDecimals: 2, shouldInsertCommas: true }) : '' }}</td>
              <td
                v-show="shownColumns.date"
                class="date align-right"
              >{{ row.date ?? 'Unknown' }}</td>
            </tr>
            <tr v-if="!rowData || !rowData.length">
              <td :colspan="COLUMNS.length" class="empty-data-table-message">
                No matching records found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useLoadSymbolOnly } from '@/composables/useLoadSymbolOnly';
  import { useChartListSummary } from '@/composables/useChartListSummary';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { useModes } from '@/composables/useModes';
  import { useModals } from '@/composables/useModals';
  import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
  import { INVALID_CHART_LIST_RECID } from '@/composables/isValidChartListRecid';
  import { fetchChartListSummary } from '@/composables/fetchChartListSummary';
  import ViewAsDropdownMenu from '@/components/dropdowns/ViewAsDropdownMenu.vue';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import ChartListSummaryColumnVisibilityDropdownMenu from '@/components/dropdowns/ChartListSummaryColumnVisibilityDropdownMenu.vue';
  import { ChartList } from '@/interfaces/chartList';
  import { SimpleFrontendChart } from '@/interfaces/chart';
  import { JsonLayerSymbolSummary, PerfSummaryQuote } from '@/interfaces/chartListSummary';
  import { sortObjectsClosure } from '@/utils/sortObjectsClosure';
  import { formatNumber } from '@/utils/formatNumber';

  interface RelevantSymbolSummaryFields {
    symbol: string;
    sector: string | null;
    industry: string | null;
    last: number | null;
    change: number | null;
    percentChange: number | null;
    changeBarGradientCoefficient: number | null;
    marketCap: number | null;
    sctr: number | null;
    universe: string;
    universeNumericSize: number; // used for sorting
    volume: number | null;
    emaTwenty: number | null;
    smaFifty: number | null;
    smaTwoHundred: number | null;
    emaTwentyDifferencePercentage: number | null;
    smaFiftyDifferencePercentage: number | null;
    smaTwoHundredDifferencePercentage: number | null;
    rsi: number | null;
    atr: number | null;
    adx: number | null;
    dividendTTM: number | null;
    yieldTTM: number | null;
    dividendFTM: number | null;
    yieldFTM: number | null;
    dividendExDate: string | null;
    dividendExDateNumericValue: number | null;
    nextEarnings: string | null;
    nextEarningsNumericValue: number | null;
    lastEarnings: string | null;
    lastEarningsNumericValue: number | null;
    earningsTime: string | null;
    epsTTM: number | null;
    priceOverEarnings: number | null;
    date: string | null;
    dateNumericValue: number | null;
  }

  type SortableColumns = keyof RelevantSymbolSummaryFields | 'htmlSanitizedName';
  type PeriodKey = keyof PerfSummaryQuote;

  const SORTED_UNIVERSE_ORDER = ['', 'etf', 'lrg', 'lse', 'mid', 'nse', 'sml', 'tsx'];
  const UNIVERSE_SIZE_MAP = SORTED_UNIVERSE_ORDER.reduce((acc, universe, index) => {
    acc[universe] = index + 1;

    return acc;
  }, {} as Record<string, number>);

  const savedChartsStore = useSavedChartsStore();
  const { chartLists } = storeToRefs(savedChartsStore);
  const { isLoggedIn, chartCode } = useAuthentication();
  const { isLoadSymbolOnlyEnabled, setIsLoadSymbolOnlyEnabled } = useLoadSymbolOnly();
  const { COLUMNS, shownColumns } = useChartListSummary();
  const { loadSimpleFrontendChart } = useLoadChartsAndChartStyles();
  const { CHART_LIST_SUMMARY_MODE, currentModeId } = useModes();
  const { launchCreateAccountModal, launchCreateChartListModal, launchApplyChartStyleToChartsModal } = useModals();
  const selectedChartListRecid = ref(INVALID_CHART_LIST_RECID);
  const selectedRows = ref<SimpleFrontendChart[]>([]);
  const symbolSummaryDataState = ref<'loading' | 'loaded' | 'error'>('loaded');
  const periodType = ref<PeriodKey>('now');
  const currentChartListSummary = ref<JsonLayerSymbolSummary[] | null>(null);
  const sortKey = ref<SortableColumns>('htmlSanitizedName');
  const sortDirection = ref<'ascending' | 'descending'>('ascending');
  const filterText = ref('');

  const selectedChartList = computed<ChartList | null>(() => {
    return chartLists.value.find(chartList => chartList.recid === selectedChartListRecid.value) ?? null;
  });

  const isSymbolSummaryModeLoading = computed(() => {
    return selectedChartList.value?.isAttemptingToLoadCharts || symbolSummaryDataState.value === 'loading';
  });

  const hasSymbolSummaryModeHadAnError = computed(() => {
    return savedChartsStore.hasFailedToLoadChartLists || symbolSummaryDataState.value === 'error';
  });

  const chartsOfSelectedChartList = computed(() => selectedChartList.value?.charts ?? []);

  const hasChartListsToShow = computed(() => {
    return !!chartLists.value.filter(chartList => !chartList.isFrameworkChartList).length && !savedChartsStore.isAttemptingToLoadChartLists;
  });

  const areAnyRowsVisible = computed(() => {
    return Object.values(shownColumns).some(isColumnVisible => isColumnVisible);
  });

  const selectedRowRecids = computed(() => {
    return selectedRows.value.map(row => row.recid);
  });

  const symbolSummarySymbolDataMap = computed(() => {
    if (!relevantSymbolSummaryFields.value)
      return null;

    const symbolToSummaryDataMap = relevantSymbolSummaryFields.value.reduce((acc, curr) => {
      acc[curr.symbol.toUpperCase()] = curr;

      return acc;
    }, {} as Record<string, RelevantSymbolSummaryFields>);

    return symbolToSummaryDataMap;
  });

  const chartListSymbolSummaryCharts = computed(() => {
    if (!selectedChartList.value || !symbolSummarySymbolDataMap.value) {
      return null;
    }

    const rows = [];

    for (const chart of selectedChartList.value.charts) {
      rows.push({
        ...(symbolSummarySymbolDataMap.value[chart.symbol.toUpperCase()] ?? {}),
        ...chart
      });
    }

    return rows;
  });

  const rowData = computed(() => {
    if (!chartListSymbolSummaryCharts.value) {
      return null;
    }

    const charts = chartListSymbolSummaryCharts.value.filter((chart) => {
      return (
        filterText.value === '' ||
        chart.symbol.toLowerCase().includes(filterText.value.toLowerCase()) ||
        chart.name.toLowerCase().includes(filterText.value.toLowerCase())
      );
    });

    charts.sort(sortObjectsClosure({
      propertyName: sortKey.value,
      order: sortDirection.value
    }));

    return charts;
  });

  const relevantSymbolSummaryFields = computed(() => {
    if (!currentChartListSummary.value) {
      return null;
    }

    const positivePercentChanges = currentChartListSummary.value.map(row => {
      return !!row?.perfSummaryQuote ? row.perfSummaryQuote[periodType.value]?.pct : null;
    })
      .filter(change => typeof change === 'number')
      .map(change => Math.abs(change));
    const maxPercentChange = Math.max(...positivePercentChanges);

    const relevantFieldsArray = currentChartListSummary.value.map<RelevantSymbolSummaryFields>(symbolSummary => {
      const intradaySummaryQuote = symbolSummary?.perfSummaryQuote?.now ?? null;
      const specifiedPeriodSummaryQuote = !!symbolSummary?.perfSummaryQuote ? symbolSummary.perfSummaryQuote[periodType.value] ?? null : null;
      const getDifferencePercentage = (technicalValue: number | null) => {
        return typeof technicalValue === 'number' && typeof intradaySummaryQuote?.price === 'number' ? getTechnicalPercentage(technicalValue, intradaySummaryQuote.price) : null;
      }

      const nextEarningsDate = typeof symbolSummary?.nextEarningsDate === 'string' ? getDateFromString(symbolSummary.nextEarningsDate) : null;
      const lastEarningsDate = typeof symbolSummary?.lastEarningsDate === 'string' ? getDateFromString(symbolSummary.lastEarningsDate) : null;
      const dividendExDate = typeof symbolSummary?.dividendExDate === 'string' ? getDateFromString(symbolSummary.dividendExDate) : null;
      const date = typeof intradaySummaryQuote?.date === 'string' ? getShortenedDateFromString(intradaySummaryQuote.date) : null;

      return {
        symbol: symbolSummary.symbol,
        sector: symbolSummary?.companyInfo?.sector ?? null,
        industry: symbolSummary?.companyInfo?.industry ?? null,
        last: intradaySummaryQuote?.price ?? null,
        change: specifiedPeriodSummaryQuote?.chg ?? null,
        percentChange: specifiedPeriodSummaryQuote?.pct ?? null,
        changeBarGradientCoefficient: typeof specifiedPeriodSummaryQuote?.pct === 'number' ? specifiedPeriodSummaryQuote.pct / maxPercentChange : null,
        marketCap: symbolSummary.marketCap,
        sctr: symbolSummary.sctr ?? null,
        universe: symbolSummary.universe ?? '',
        universeNumericSize: getUniverseNumericSize(symbolSummary.universe ?? ''), // used for sorting
        volume: symbolSummary.intradayVolume,
        emaTwenty: symbolSummary.ema20,
        smaFifty: symbolSummary.sma50,
        smaTwoHundred: symbolSummary.sma200,
        emaTwentyDifferencePercentage: getDifferencePercentage(symbolSummary.ema20),
        smaFiftyDifferencePercentage: getDifferencePercentage(symbolSummary.sma50),
        smaTwoHundredDifferencePercentage: getDifferencePercentage(symbolSummary.sma200),
        rsi: symbolSummary.rsi,
        atr: symbolSummary.atr,
        adx: symbolSummary.adx,
        dividendTTM: symbolSummary.dividendRate,
        yieldTTM: symbolSummary.dividendYield,
        dividendFTM: symbolSummary.dividendRateForecast,
        yieldFTM: symbolSummary.dividendYieldForecast,
        dividendExDate: dividendExDate !== null ? formatDate(dividendExDate) : null,
        dividendExDateNumericValue: dividendExDate?.getTime() ?? null,
        nextEarnings: nextEarningsDate !== null ? formatDate(nextEarningsDate) : null,
        nextEarningsNumericValue: nextEarningsDate?.getTime() ?? null,
        lastEarnings: lastEarningsDate !== null ? formatDate(lastEarningsDate) : null,
        lastEarningsNumericValue: lastEarningsDate?.getTime() ?? null,
        earningsTime: symbolSummary.earningsTime,
        epsTTM: symbolSummary.eps,
        priceOverEarnings: symbolSummary.pe,
        date: date !== null ? formatShortenedDate(date) : null,
        dateNumericValue: date?.getTime() ?? null
      }
    });

    return relevantFieldsArray;
  });

  function setSorting(key: SortableColumns, direction: 'ascending' | 'descending') {
    if (sortKey.value === key) {
      sortDirection.value = sortDirection.value === 'ascending' ? 'descending' : 'ascending';
    } else {
      sortKey.value = key;
      sortDirection.value = direction;
    }
  }

  function getDateFromString(dateString: string) {
    dateString = dateString.includes('T') ? dateString : dateString + 'T00:00:00';

    return new Date(dateString);
  }

  function formatDate(dateObj: Date): string {
    const month = `${dateObj.getMonth() + 1}`.padStart(2, '0');
    const day = `${dateObj.getDate()}`.padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${month}/${day}/${year}`;
  }

  function getShortenedDateFromString(dateString: string): Date {
    const [date, time] = dateString.split(/\s/).filter(dateStringPart => !!dateStringPart); // format is: MM-DD-YYYY mm:ss

    return time ? new Date(dateString) : new Date(date + ' 16:00'); // missing timestamp values will be 'close' values so use the end of day timestamp
  }

  function formatShortenedDate(dateObj: Date): string {
    if (isNaN(dateObj.getTime())) {
      return 'Unknown';
    }

    const month = `${dateObj.getMonth() + 1}`.padStart(2, '0');
    const day = `${dateObj.getDate()}`.padStart(2, '0');
    const hours = `${dateObj.getHours()}`.padStart(2, '0');
    const minutes = `${dateObj.getMinutes()}`.padStart(2, '0');
    return `${month}-${day}, ${hours}:${minutes}`;
  }

  function getUniverseNumericSize(universe: string): number {
    return UNIVERSE_SIZE_MAP[universe.toLowerCase()] ?? 0;
  }

  function getTechnicalPercentage(value: number, close: number) {
    const diff = close - value;
    const pct = (Math.abs(diff) / value) * 100;
    const returnedPCT = diff < 0 ? pct * -1 : pct;

    return returnedPCT;
  }

  function formatMarketCap(num: number): string {
    if (num === 0) {
      return '0';
    }

    const FORMAT_NUMBER_OPTIONS = { desiredNumberOfDecimals: 3, shouldInsertCommas: true }
    const numberReduced = num / 1000;

    if (numberReduced > 1000000000) {
      return formatNumber(numberReduced / 1000000000, FORMAT_NUMBER_OPTIONS) + ' T';
    }

    if (numberReduced > 1000000) {
      return formatNumber(numberReduced / 1000000, FORMAT_NUMBER_OPTIONS) + ' B';
    }

    if (numberReduced > 1000) {
      return formatNumber(numberReduced / 1000, FORMAT_NUMBER_OPTIONS) + ' M';
    }

    if (numberReduced > 1) {
      return formatNumber(numberReduced, FORMAT_NUMBER_OPTIONS) + ' K';
    }

    return formatNumber(numberReduced * 1000, FORMAT_NUMBER_OPTIONS) + 'K';
  }

  function toggleAllRows() {
    if (selectedChartList.value?.charts.length && selectedRows.value.length > selectedChartList.value.charts.length / 2) { // when we have more than half selected, deselect all
      selectedRows.value = [];
      return;
    }

    selectedRows.value = [ ...(selectedChartList.value?.charts ?? []) ];
  }

  function handleRowSelectionChange(value: boolean, row: SimpleFrontendChart) {
    if (value && !selectedRowRecids.value.includes(row.recid)) {
      selectedRows.value.push(row);
      return;
    }

    const removeRow = selectedRows.value.find(selectedRow => selectedRow.recid === row.recid);
    const removeIndex = removeRow ? selectedRows.value.indexOf(removeRow) : -1;

    if (removeIndex !== -1) {
      selectedRows.value.splice(removeIndex, 1);
    }
  }

  async function loadChartListSummaryData(chartList: ChartList | null) {
    if (!chartList?.charts.length) { // json layer will give an error if the chart list is empty
      return;
    }

    symbolSummaryDataState.value = 'loading';

    try {
      const listNumCopy = chartList.listNum;
      const chartListSummary = await fetchChartListSummary(chartList.listNum.toString());

      if (chartList.listNum !== listNumCopy) {
        return;
      }

      currentChartListSummary.value = chartListSummary;
      symbolSummaryDataState.value = 'loaded';
    } catch (e) {
      symbolSummaryDataState.value = 'error';

      window.sc3WorkbenchLogger.error('Error while fetching ChartList summary', e);
    }
  }

  async function applyChartStyleToCharts() {
    if (!isLoggedIn.value) {
      launchCreateAccountModal();
      return;
    }

    const chartList = selectedChartList.value;

    if (
      !chartList?.charts.length ||
      !selectedRows.value.length ||
      chartList.isReadonly
    ) {
      return;
    }

    await loadChartsForChartList(chartList.recid, chartList.listNum, true);

    launchApplyChartStyleToChartsModal(chartList, selectedRows.value);
  }

  function initializeSelectedChartRecid() {
    if (selectedChartList.value) {
      return;
    }

    const firstAvailableChartList = chartLists.value.find(chartList => !chartList.isFrameworkChartList);

    if (!firstAvailableChartList) {
      return;
    }

    selectedChartListRecid.value = firstAvailableChartList.recid;
  }

  async function handleCurrentModeIdOrSelectedChartListChanged(newValue: string | ChartList | null) {
    if (currentModeId.value !== CHART_LIST_SUMMARY_MODE.id) {
      return;
    }

    const wasSelectedChartListChange = typeof newValue === 'object' && newValue !== null;

    if (wasSelectedChartListChange) {
      selectedRows.value = [];
      filterText.value = '';
    }

    if (!selectedChartList.value) {
      return;
    }

    await loadChartsForChartList(selectedChartList.value.recid, selectedChartList.value.listNum, false);
    await loadChartListSummaryData(selectedChartList.value);
  }

  watch(currentModeId, handleCurrentModeIdOrSelectedChartListChanged);
  watch(selectedChartList, handleCurrentModeIdOrSelectedChartListChanged);
  watch(chartLists, initializeSelectedChartRecid);
  onMounted(initializeSelectedChartRecid);
</script>

<style scoped>
  .chart-list-secondary-control-parent {
    display: flex;
    align-items: end;
    flex-wrap: wrap;
    gap: 5px 0px;
    grid-row: 1;
    grid-column: 1;
  }

  .primary-summary-table-controls-parent {
    display: flex;
    align-items: end;
    flex-wrap: wrap;
    gap: 5px 0px;
    grid-row: 2;
    grid-column: 1;
  }

  .chart-list-summary-ui-components {
    font-size: 0px;
    padding: 0px 10px 10px 10px;
  }

  .chart-list-summary-ui-upper-bar {
    padding-top: 6px;
    display: grid;
    grid-gap: 5px;
    grid-template-columns: 1fr;
  }

  button.apply-chart-style-button:disabled {
    opacity: 0.4 !important;
  }

  .chart-list-summary-table-container {
    padding: 0px 10px 10px 10px;
    max-width: 100%;
    overflow: auto;
  }

  #view-list-as-dropdown {
    width: 150px;
  }

  .loading-block {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
  }

  .loading-block i {
    font-size: 50px;
  }

  .chart-list-summary-control-section {
    display: flex;
    align-items: end;
    flex-wrap: wrap;
    gap: 5px 0px;
  }

  .chart-list-summary-group-message {
    font-size: 18px;
    text-align: center;
    font-weight: 700;
    margin: 0px;
    padding: 20px;
  }

  .chart-list-summary-group-message-small-note {
    font-size: 16px;
  }

  .show-symbols-only-checkbox-container {
    width: fit-content;
    height: fit-content;
    align-self: center;
    margin: 4px 0px 4px auto;
    padding-top: 8px;
  }

  .search-chart-list-summary-parent {
    margin-left: auto;
  }

  .error-message {
    border: 1px solid #f5c6cb;
    color: #721c24;
    background-color: #f8d7da;
    font-weight: 700;
    border-radius: 4px;
    margin: 8px;
    line-height: 2;
    font-size: 16px;
    padding: 8px;
    text-align: center;
  }

  .align-right {
    text-align: right;
  }

  table.data-table {
    width: calc(100% - 2px);
  }

  th.select-all-rows-header-container {
    width: 50px;
  }

  .data-table button.select-all-button {
    all: unset;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .data-table .select-all-button:hover {
    cursor: pointer;
  }

  .data-table tr:last-of-type td {
    border-bottom: 1px solid #c0ced8 !important;
  }

  .data-table td:last-of-type, th:last-of-type {
    border-right: 1px solid #c0ced8 !important;
  }

  table.sc3-table.sc3-table-centered thead tr th,
  table.sc3-table.sc3-table-centered tbody tr td {
    text-align: center;
  }

  .data-table button.clickable-row-text {
    color: #0f4d8a;
    font-size: 14px;
    font-weight: 700;
    background: none;
    border: none;
  }

  table.sc3-table tbody tr td {
    font-weight: normal;
    white-space: nowrap;
  }

  table.sc3-table tbody tr td.empty-data-table-message {
    padding: 15px 10px;
    font-weight: 700;
    color: #cc000e;
    text-align: center;
  }

  .data-table th.sortable {
    cursor: pointer;
  }

  .data-table th.sortable .sortable-header-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  .plus-minus-change-container {
    width: 80px;
  }

  table.sc3-table tbody tr:nth-of-type(odd) td {
    background-color: #f2f5f7;
  }

  table.sc3-table tbody tr:hover td {
    background-color: #dfe7ec;
  }

  table.sc3-table tbody tr td.green-font,
  table.sc3-table tbody tr td span.green-font {
    color: #1e824c;
  }

  table.sc3-table tbody tr td.red-font,
  table.sc3-table tbody tr td span.red-font {
    color: #cc000e;
  }

  td.sctr {
    font-weight: 900 !important;
  }

  td.sctr-zero-to-twenty {
    background-color: #ffb3b5 !important;
    color: #660007;
  }

  td.sctr-twenty-to-fourty {
    background-color: #ffe6e7 !important;
    color: #cc000e;
  }

  td.sctr-fourty-to-sixty {
    background-color: #fef9e6 !important;
    color: #c59f07;
  }

  td.sctr-sixty-to-eighty {
    background-color: #eafaf2 !important;
    color: #27a562;
  }

  td.sctr-eighty-to-ninety {
    background-color: #d6f5e4 !important;
    color: #1d7c49;
  }

  td.sctr-ninety-plus {
    background-color: #c1f0d7 !important;
    color: #135331;
  }

  td.date {
    font-size: smaller;
    color: #707f8f;
  }

  .gradient-bar {
    display: inline-block;
    height: 10px;
  }

  .red-gradient-bar {
    background: linear-gradient(#900000, #FF0000, #900000)
  }

  .green-gradient-bar {
    background: linear-gradient(#069006, #42ff42, #069006);
  }
</style>
